import { ShieldCheck, Workflow, LineChart, Landmark, Scale } from "lucide-react";

export const SERVICES = [
  {
    slug: "auditing",
    name: "Auditing",
    icon: ShieldCheck,
    image: "/images/auditing.jpg",
    imageAlt: "Financial data and performance charts displayed on a monitor",
    summary:
      "Strengthen assurance, improve control environments, and gain independent perspectives on business and technology risks.",
    items: [
      "External audit support",
      "Internal audit and operational assurance",
      "IT audit and technology risk",
      "Financial controls and risk assessments",
      "Forensic and investigative assignments",
    ],
    heroTitle: "Independent assurance, applied with precision.",
    heroIntro:
      "Access experienced audit and assurance practitioners for internal audit co-sourcing, technology risk reviews, controls assessments and investigative work — scoped around your environment and timetable.",
    overview: [
      "Audit and assurance requirements rarely arrive in neat, predictable volumes. A new regulatory focus, a systems migration, an acquisition or a gap in the internal audit plan can create demand for specialist capability that is difficult to hold permanently in-house.",
      "Through the Trusted Leap network, organisations can be introduced to independent practitioners with relevant audit, controls and technology risk experience. Each introduction is shaped around the scope, industry context and independence considerations of the assignment.",
    ],
    challenges: [
      "An internal audit plan that exceeds current team capacity or specialist skills",
      "Technology risk, cybersecurity or ERP control reviews requiring niche expertise",
      "Control environments that need an independent assessment before an external audit cycle",
      "Readiness work ahead of a listing, financing or regulatory review",
      "Sensitive matters requiring discreet investigative or forensic support",
    ],
    expertise: [
      "Internal audit leaders and co-sourced audit practitioners",
      "IT auditors and technology risk specialists",
      "Financial controls and SOX-style controls practitioners",
      "Risk assessment and assurance mapping specialists",
      "Forensic accountants and investigation professionals",
    ],
    scenarios: [
      { title: "Co-sourced internal audit", text: "A specialist joins an existing internal audit function to deliver defined reviews within the annual plan." },
      { title: "ERP controls review", text: "An IT audit practitioner assesses access, change management and automated controls following a systems implementation." },
      { title: "Pre-audit controls assessment", text: "An independent reviewer evaluates key financial controls and documentation ahead of an external audit." },
      { title: "Investigative support", text: "A forensic professional supports a confidential fact-finding exercise under an agreed engagement framework." },
    ],
    regulatory:
      "Statutory audit and certain assurance services may only be performed by registered firms or individuals holding the relevant authorisations in a given jurisdiction. Availability of any service depends on the professional's qualifications, independence position, jurisdiction and the agreed engagement terms.",
    faqs: [
      { q: "Can Trusted Leap provide a statutory audit opinion?", a: "Trusted Leap does not itself issue audit opinions. Where a statutory audit is required, it must be performed by an appropriately registered auditor. The network can support related needs such as audit readiness, internal audit and controls work, subject to the professional's qualifications." },
      { q: "How are independence considerations handled?", a: "Independence and conflicts are considered during the suitability assessment. Professionals are asked to disclose relationships relevant to the engagement, and the client should also confirm any requirements specific to its own regulatory position." },
      { q: "Can a specialist work alongside our internal audit team?", a: "Yes. Many requests are for co-sourced support, where an independent practitioner delivers specific reviews under the direction of the client's head of internal audit." },
    ],
  },
  {
    slug: "consulting",
    name: "Consulting",
    icon: Workflow,
    image: "/images/consulting.jpg",
    imageAlt: "Consultants reviewing documents together around a meeting table",
    summary:
      "Solve complex business challenges with specialists who bring practical experience and a focused delivery approach.",
    items: [
      "Business and operating model transformation",
      "Strategy and operational improvement",
      "Enterprise systems and digital transformation",
      "Process optimisation",
      "Governance, risk, and controls improvement",
    ],
    heroTitle: "Practical expertise for consequential change.",
    heroIntro:
      "Bring in senior independent consultants who have led transformation, systems and operational improvement work — without the overhead of a large programme team where one is not needed.",
    overview: [
      "Transformation agendas often depend on a small number of individuals with the right combination of experience: people who have redesigned operating models, stabilised enterprise system programmes or rebuilt processes under real constraints.",
      "Trusted Leap helps organisations identify independent consultants whose background aligns with the change being undertaken, whether the need is a focused diagnostic, interim leadership of a workstream or hands-on delivery support.",
    ],
    challenges: [
      "A transformation programme that needs experienced leadership for a specific workstream",
      "Enterprise system or digital initiatives at risk of delay or scope drift",
      "Operating costs or process inefficiency that require an objective diagnostic",
      "Governance and control frameworks that have not kept pace with growth",
      "Strategic options that need structured analysis before a board decision",
    ],
    expertise: [
      "Operating model and organisation design specialists",
      "Programme and transformation leaders",
      "ERP, finance systems and digital transformation advisers",
      "Process improvement and operational excellence practitioners",
      "Governance, risk and compliance consultants",
    ],
    scenarios: [
      { title: "Operating model diagnostic", text: "A consultant assesses current structures and processes and sets out options for a future operating model." },
      { title: "Programme recovery", text: "An experienced practitioner reviews a delayed systems programme and supports a revised delivery plan." },
      { title: "Finance process redesign", text: "A specialist streamlines record-to-report or procure-to-pay processes with the client's finance team." },
      { title: "GRC framework uplift", text: "A governance professional helps design a proportionate risk and controls framework for a scaling business." },
    ],
    regulatory:
      "Consulting engagements are delivered by independent professionals under the engagement terms agreed between the parties. Where a consulting scope touches regulated activities, the relevant qualifications and authorisations will need to be confirmed for that jurisdiction.",
    faqs: [
      { q: "Is this suited to large programmes or small, focused assignments?", a: "Both can be considered. The network is particularly well suited to focused assignments that benefit from senior, experienced individuals rather than large teams." },
      { q: "Can consultants work on-site?", a: "Working arrangements — on-site, remote or hybrid — are agreed as part of the scope and depend on the professional's availability and location." },
      { q: "Who manages the consultant day to day?", a: "Day-to-day direction is typically agreed between the client and the professional within the engagement terms. Trusted Leap supports coordination of the engagement process." },
    ],
  },
  {
    slug: "advisory",
    name: "Advisory",
    icon: LineChart,
    image: "/images/advisory.jpg",
    imageAlt: "An empty boardroom with a long table overlooking a city",
    summary:
      "Access targeted expertise for critical decisions, complex transactions, and business transformation.",
    items: [
      "Mergers and acquisitions support",
      "Financial restructuring",
      "Transaction support",
      "Financial and commercial analysis",
      "Strategic business advisory",
    ],
    heroTitle: "Clear counsel for decisions that matter.",
    heroIntro:
      "Connect with independent advisers experienced in transactions, restructuring and commercial analysis — professionals who can support leadership teams through high-stakes decisions.",
    overview: [
      "Acquisitions, divestments, refinancings and restructurings place unusual demands on leadership teams. The quality of analysis and the experience of the people involved can materially shape how options are understood and evaluated.",
      "Trusted Leap introduces organisations to independent advisers who have worked on comparable situations, enabling focused support for due diligence, financial modelling, integration planning or strategic reviews.",
    ],
    challenges: [
      "An acquisition or investment that requires independent financial and commercial review",
      "Integration or separation planning following a transaction",
      "Liquidity pressure or a need to evaluate restructuring options",
      "Board-level decisions that require robust financial analysis",
      "Strategic reviews of portfolios, markets or business units",
    ],
    expertise: [
      "Transaction services and due diligence professionals",
      "Corporate finance and M&A practitioners",
      "Restructuring and turnaround specialists",
      "Financial modelling and valuation analysts",
      "Commercial and strategic advisers",
    ],
    scenarios: [
      { title: "Buy-side financial review", text: "An adviser supports the review of a target's quality of earnings, working capital and key financial risks." },
      { title: "Post-deal integration", text: "An experienced practitioner helps plan finance and operational integration in the first months after completion." },
      { title: "Restructuring options", text: "A specialist analyses cash flow forecasts and sets out options for discussion with stakeholders." },
      { title: "Investment case analysis", text: "An adviser builds and tests a financial model supporting a capital allocation decision." },
    ],
    regulatory:
      "Certain corporate finance, investment and insolvency activities are regulated and may only be carried out by authorised or licensed persons in a given jurisdiction. Availability depends on each professional's qualifications, authorisations and the agreed engagement terms. Advisory support does not constitute a guarantee of any transaction or financial outcome.",
    faqs: [
      { q: "Can advisers act as regulated corporate finance intermediaries?", a: "Only where the individual or their firm holds the relevant authorisation in the jurisdiction concerned. This is confirmed as part of the suitability assessment for any regulated scope." },
      { q: "How is confidentiality handled for transaction work?", a: "Confidentiality arrangements, including non-disclosure agreements where appropriate, are put in place before sensitive information is shared with a professional." },
      { q: "Can support be arranged quickly for a live transaction?", a: "We aim to prepare a tailored shortlist within 72 hours of a sufficiently detailed request, subject to complexity and the availability of suitable expertise." },
    ],
  },
  {
    slug: "tax",
    name: "Tax",
    icon: Landmark,
    image: "/images/tax.jpg",
    imageAlt: "A professional reviewing financial figures with a calculator on a desk",
    summary:
      "Navigate complex tax requirements with appropriately qualified professionals familiar with the relevant jurisdiction.",
    items: [
      "International and cross-border tax advisory",
      "Tax compliance coordination",
      "Tax structuring",
      "Indirect and direct tax advisory",
      "Tax controversy support",
    ],
    heroTitle: "Tax expertise, matched to jurisdiction.",
    heroIntro:
      "Find appropriately qualified tax professionals for cross-border questions, structuring considerations, indirect tax matters and compliance coordination across the jurisdictions in which you operate.",
    overview: [
      "Tax questions are shaped by jurisdiction, industry and the specific facts of a business. As organisations expand into new markets, the need for practitioners who understand local rules — and how they interact across borders — becomes increasingly important.",
      "Trusted Leap works to identify tax professionals whose qualifications and experience correspond to the jurisdictions and subject matter involved, and supports coordination where more than one specialist is required.",
    ],
    challenges: [
      "Expansion into a new market with unfamiliar direct and indirect tax obligations",
      "Cross-border arrangements that require coordinated advice across jurisdictions",
      "Indirect tax (VAT/GST/sales tax) questions arising from new business models",
      "Compliance obligations spread across multiple entities and advisers",
      "Enquiries or disputes with tax authorities that require experienced support",
    ],
    expertise: [
      "International and cross-border tax advisers",
      "Indirect tax specialists",
      "Corporate and direct tax practitioners",
      "Transfer pricing professionals",
      "Tax controversy and dispute support specialists",
    ],
    scenarios: [
      { title: "Market entry review", text: "A tax professional outlines the principal tax considerations of establishing operations in a new jurisdiction." },
      { title: "Indirect tax assessment", text: "A specialist reviews how VAT or GST applies to a new product, channel or supply chain." },
      { title: "Compliance coordination", text: "A practitioner helps coordinate filing obligations across a group's entities and local advisers." },
      { title: "Authority enquiry support", text: "An experienced professional supports the preparation of responses to a tax authority enquiry." },
    ],
    regulatory:
      "Tax advice and representation before tax authorities may require specific registrations or qualifications that vary by jurisdiction. Availability of any service depends on the professional's qualifications, authorisations, jurisdiction and the agreed engagement terms. Information on this website is not tax advice.",
    faqs: [
      { q: "Can one professional advise on several countries?", a: "Some professionals have multi-jurisdictional experience, but advice on each jurisdiction should come from someone appropriately qualified for it. Where needed, several specialists may be identified and coordinated." },
      { q: "Do tax professionals in the network represent clients before authorities?", a: "Representation rights depend on local rules and the individual's registrations. This is confirmed for each relevant jurisdiction before an engagement proceeds." },
      { q: "Is the information on this page tax advice?", a: "No. This page describes the types of expertise that may be available through the network. Tax advice is provided only by qualified professionals under engagement terms." },
    ],
  },
  {
    slug: "law",
    name: "Law",
    icon: Scale,
    image: "/images/law.jpg",
    imageAlt: "A professional reviewing and signing a written agreement",
    summary:
      "Connect with appropriately qualified legal practitioners for jurisdiction-specific legal requirements.",
    items: [
      "Commercial contract drafting and review",
      "Corporate and commercial legal advisory",
      "Regulatory guidance",
      "Dispute support",
      "Cross-border legal coordination",
    ],
    heroTitle: "Qualified legal support, where you need it.",
    heroIntro:
      "Connect with appropriately qualified legal practitioners for commercial contracts, corporate matters, regulatory questions and cross-border coordination — subject to their admission and authorisation in the relevant jurisdiction.",
    overview: [
      "In-house legal teams and business leaders frequently need specialist legal input that sits outside their core expertise or capacity: a complex supply agreement, a regulatory question in a new market, or coordination of advice across several jurisdictions.",
      "Trusted Leap aims to connect organisations with legal practitioners whose admission, experience and subject-matter expertise correspond to the requirement. Legal services are provided by the practitioner under their own professional obligations and engagement terms.",
    ],
    challenges: [
      "High volumes of commercial contracts requiring experienced review",
      "Corporate or commercial matters in a jurisdiction where you lack local counsel",
      "Regulatory questions arising from new products, markets or business models",
      "Disputes that require experienced support alongside existing counsel",
      "Cross-border matters that require coordination across several legal advisers",
    ],
    expertise: [
      "Commercial and contracts lawyers",
      "Corporate and commercial legal advisers",
      "Regulatory specialists",
      "Dispute resolution practitioners",
      "Cross-border legal coordinators",
    ],
    scenarios: [
      { title: "Contract review support", text: "A commercial lawyer reviews and negotiates key supplier or customer agreements alongside the in-house team." },
      { title: "Local counsel identification", text: "A practitioner admitted in the relevant jurisdiction advises on a defined corporate matter." },
      { title: "Regulatory guidance", text: "A regulatory specialist outlines the legal framework applicable to a proposed product launch." },
      { title: "Multi-jurisdiction coordination", text: "A lead practitioner coordinates input from qualified lawyers across several countries." },
    ],
    regulatory:
      "Legal services may only be provided by practitioners who are appropriately admitted and authorised in the relevant jurisdiction, and are subject to local professional conduct rules. Trusted Leap is not a law firm and does not provide legal advice. Availability depends on the practitioner's qualifications, authorisations, jurisdiction and engagement terms.",
    faqs: [
      { q: "Is Trusted Leap a law firm?", a: "No. Trusted Leap facilitates introductions to independent legal practitioners. Any legal advice is provided by the practitioner under their own professional obligations and engagement terms." },
      { q: "How is a lawyer's admission confirmed?", a: "Professionals are asked to provide details of their admission and registration. Where a matter proceeds, the relevant status should be verified for the specific jurisdiction before engagement." },
      { q: "How are conflicts of interest managed?", a: "Legal practitioners are responsible for conducting conflict checks in accordance with their professional rules. Trusted Leap's process also asks professionals to disclose any known conflicts before an introduction." },
    ],
  },
];

export const getService = (slug) => SERVICES.find((s) => s.slug === slug);
